import { Text, View } from "@react-pdf/renderer";
import type { Style } from '@react-pdf/types';
import { colors } from "../../color";
import { PillsColor } from "../../molecoles/pills/Pills.types";

export const pillVariantKey = (emphasis: boolean, outline: boolean): string => {
    return `${emphasis}-${outline}`;
};

export const colorKey = (color: PillsColor): { pillContainerVariant: Map<string, Style>, contentColor: Map<string, string> } => {
    switch (color) {
        case 'gray':
            return (
                {
                    pillContainerVariant:
                        new Map<string, Style>([
                            [pillVariantKey(true, true), {
                                backgroundColor: colors.neutral[100],
                                borderColor: colors.neutral[500]
                            }],
                            [pillVariantKey(true, false), {
                                backgroundColor: colors.neutral[500],
                                borderColor: colors.neutral[500]
                            }],
                            [pillVariantKey(false, true), {
                                backgroundColor: colors.neutral[50],
                                borderColor: colors.neutral[300]
                            }],
                            [pillVariantKey(false, false), {
                                backgroundColor: colors.neutral[100],
                                borderColor: colors.neutral[100]
                            }]
                        ]),
                    contentColor:
                        new Map<string, string>([
                            [pillVariantKey(true, true), colors.neutral[500]],
                            [pillVariantKey(true, false), colors.white],
                            [pillVariantKey(false, true), colors.neutral[600]],
                            [pillVariantKey(false, false), colors.neutral[800]]
                        ])
                })
        case 'blue':
            return (
                {
                    pillContainerVariant:
                        new Map<string, Style>([
                            [pillVariantKey(true, true), {
                                backgroundColor: colors.brandPrimary[100],
                                borderColor: colors.brandPrimary[500]
                            }],
                            [pillVariantKey(true, false), {
                                backgroundColor: colors.brandPrimary[500],
                                borderColor: colors.brandPrimary[500]
                            }],
                            [pillVariantKey(false, true), {
                                backgroundColor: colors.brandPrimary[50],
                                borderColor: colors.brandPrimary[300]
                            }],
                            [pillVariantKey(false, false), {
                                backgroundColor: colors.brandPrimary[100],
                                borderColor: colors.brandPrimary[100]
                            }]
                        ]),
                    contentColor:
                        new Map<string, string>([
                            [pillVariantKey(true, true), colors.brandPrimary[500]],
                            [pillVariantKey(true, false), colors.white],
                            [pillVariantKey(false, true), colors.brandPrimary[600]],
                            [pillVariantKey(false, false), colors.brandPrimary[800]]
                        ])
                })
        case 'green':
            return (
                {
                    pillContainerVariant:
                        new Map<string, Style>([
                            [pillVariantKey(true, true), {
                                backgroundColor: colors.green[100],
                                borderColor: colors.green[500]
                            }],
                            [pillVariantKey(true, false), {
                                backgroundColor: colors.green[500],
                                borderColor: colors.green[500]
                            }],
                            [pillVariantKey(false, true), {
                                backgroundColor: colors.green[100],
                                borderColor: colors.green[300]
                            }],
                            [pillVariantKey(false, false), {
                                backgroundColor: colors.green[100],
                                borderColor: colors.green[100]
                            }]
                        ]),
                    contentColor:
                        new Map<string, string>([
                            [pillVariantKey(true, true), colors.green[500]],
                            [pillVariantKey(true, false), colors.white],
                            [pillVariantKey(false, true), colors.green[600]],
                            [pillVariantKey(false, false), colors.green[800]]
                        ])
                })
        case 'orange':
            return (
                {
                    pillContainerVariant:
                        new Map<string, Style>([
                            [pillVariantKey(true, true), {
                                backgroundColor: colors.warning[100],
                                borderColor: colors.warning[500]
                            }],
                            [pillVariantKey(true, false), {
                                backgroundColor: colors.warning[500],
                                borderColor: colors.warning[500]
                            }],
                            [pillVariantKey(false, true), {
                                backgroundColor: colors.warning[100],
                                borderColor: colors.warning[300]
                            }],
                            [pillVariantKey(false, false), {
                                backgroundColor: colors.warning[100],
                                borderColor: colors.warning[100]
                            }]
                        ]),
                    contentColor:
                        new Map<string, string>([
                            [pillVariantKey(true, true), colors.warning[500]],
                            [pillVariantKey(true, false), colors.white],
                            [pillVariantKey(false, true), colors.warning[600]],
                            [pillVariantKey(false, false), colors.warning[800]]
                        ])
                })
        case 'red':
            return (
                {
                    pillContainerVariant:
                        new Map<string, Style>([
                            [pillVariantKey(true, true), {
                                backgroundColor: colors.red[100],
                                borderColor: colors.red[500]
                            }],
                            [pillVariantKey(true, false), {
                                backgroundColor: colors.red[500],
                                borderColor: colors.red[500]
                            }],
                            [pillVariantKey(false, true), {
                                backgroundColor: colors.red[100],
                                borderColor: colors.red[300]
                            }],
                            [pillVariantKey(false, false), {
                                backgroundColor: colors.red[100],
                                borderColor: colors.red[100]
                            }]
                        ]),
                    contentColor:
                        new Map<string, string>([
                            [pillVariantKey(true, true), colors.red[500]],
                            [pillVariantKey(true, false), colors.white],
                            [pillVariantKey(false, true), colors.red[600]],
                            [pillVariantKey(false, false), colors.red[800]]
                        ])
                })
        default:
            return ({
                pillContainerVariant: new Map<string, Style>([]),
                contentColor: new Map<string, string>([])
            })
    }
};

interface Props {
    label: string
    color: PillsColor
    size: "sm" | "md"
    outline: boolean
    emphasis: boolean
}

export function PillsPdf(props: Props) {
    const pillStyle = colorKey(props.color).pillContainerVariant.get(pillVariantKey(props.emphasis, props.outline)) ?? {};
    const textColor = colorKey(props.color).contentColor.get(pillVariantKey(props.emphasis, props.outline)) ?? colors.neutral[800];

    return (
        <View
            style={{
                ...pillStyle,
                height: props.size === "sm" ? "10px" : "16px",
                borderRadius: props.size === "sm" ? 10 : 18,
                paddingHorizontal: props.size === "sm" ? 4 : 8,
                paddingVertical: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                borderWidth: 1,
                borderStyle: "solid",
                width: "80%"
            }}
        >
            <Text style={{
                fontSize: props.size === "sm" ? '6px' : '8px',
                fontWeight: 600,
                textAlign: 'center',
                fontFamily: 'Inter',
                color: textColor
            }}>
                {props.label}
            </Text>
        </View>
    );
}